/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import { logger as log } from '@common/logger';
import { EventBusModule } from '@common/event-bus-module';
import { Sttp } from '@common/sttp';

import { CREATE_ORDER } from '@pages/BuyInsurancePage/apis/graphql';

import { Graphql } from '@common/graphql';
import { apiConfig } from '@/config/apiConfig';
import { AppNavigatorService } from '@/AppNavigator/services';
import { createOrderConfig } from '@/pages/BuyInsurancePage/config/create-order.config';
import { vappContainer } from '@/application';
import { themeConfig } from '@providers/theme';

export const FIAT_TX_SUCCESS = 'fiat-service.tx-success';
export const FIAT_RAZORPAY_TX_ERROR = 'fiat-service.tx-razorpay-error';
export const FIAT_TX_ERROR = 'fiat-service.tx-error';
export const FIAT_TX_MODAL_DISMISSED = 'fiat-service.modal-dismissed';

export interface ICreateOrderProps {
  nominee: {
    firstName?: string;
    lastName?: string;
    streetAddress?: string;
    city?: String;
    country?: string;
    telephone?: string;
    email?: string;
    aadharNo?:string;
    gender?:string;
  };
  holder: {
    firstName?: string;
    lastName?: string;
    streetAddress?: string;
    city?: string;
    country?: string;
    email?: string;
    telephone?: string;
  };
  policy?: any;
  coverage: string;
  policyPremium?: string;
  termsData?: any;
  country?: string;
}

const loginSrv = () => {
  return vappContainer.get<AppNavigatorService>(
    AppNavigatorService.displayName
  );
};

const eventBus = () => {
  return vappContainer.get<EventBusModule>(EventBusModule.displayName);
};

export interface ICreateOrderMethods extends IPluginMethodMap {
  getCreateOrderProps(args: ICreateOrderProps): Promise<any>;
}

export class CreateOrderPlugin implements IAgentPlugin {
  readonly methods: ICreateOrderMethods;

  constructor() {
    this.methods = {
      getCreateOrderProps: this.getProps.bind(this),
    };
  }

  async getProps(args: ICreateOrderProps) {
    console.log(`args in order Create`, args);
    //   return await this.submitKyc(args);
    return await this.createOrder(args);
  }

  private async createOrder(args: ICreateOrderProps) {
    console.log(`args in create order`, args);
    const order = await this.getOrderId(args);

    if (order?.id && createOrderConfig?.provider === 'razorpay') {
      const options = this.getOptions({
        key_id: apiConfig?.keyId,
        provider: createOrderConfig?.provider,
        amount: Math.round(order?.amount_due * 100),
        currency: order?.currency,
        name: createOrderConfig?.name,
        description: args?.policy?.label,
        image: createOrderConfig?.image,
        order_id: order?.id, //populate from backend response
        user_name: `${args?.holder?.firstName} ${args?.holder?.lastName}`,
        user_email: args?.holder?.email,
        user_contact: args?.holder?.telephone,
        user_address: `${args?.holder?.streetAddress}, ${args?.holder?.city}, ${args?.holder?.country}`,
        theme: themeConfig?.colors?.primary,
        retry: {
          enabled: false,
        },
        backend_endpoint: `${apiConfig?.baseURL}`, // populate verify backend end point
        policy_number: order?.policyNumber || order?.id,
        premium: args?.policy?.premium?.formatted || args?.policy?.premium?.raw,
      });
      return options;
    }
  }

  private async getOrderId(args: any) {
    console.log(`args in get Order ID`, args);
    try {
      let gql = Graphql.withBaseUrl(apiConfig.gql.url);
      // const token = getToken();
      const token = loginSrv()?.getToken();
      if (token) {
        gql = gql.withHeaders({
          authentication: token,
        });
      }
      const client = gql.getClient();
      const res = await client
        .mutation(CREATE_ORDER, {
          input: {
            coverage: parseFloat(args?.coverage),
            nominee: {
              address: {
                addressLine1: args?.nominee?.streetAddress,
                city: args?.nominee?.city,
                country: args?.nominee?.country,
              },
              email: args?.nominee?.email,
              firstName: args?.nominee?.firstName,
              lastName: args?.nominee?.lastName,
              mobileNo: args?.nominee?.telephone,
              aadharNo:args?.nominee?.aadharNo,
              gender:args?.nominee?.gender
            },
            policyOption: args?.policy?.id,
            premium: args?.policy?.premium?.raw,
          },
        })
        .toPromise();

      if (res?.data?.purchasePolicy) {
        log.log(`create policy data`, res?.data);
        return res?.data?.purchasePolicy;
      }
    } catch (err: any) {
      throw err;
    }
  }

  private getOptions({
    key_id,
    amount,
    provider,
    currency,
    name,
    description,
    image,
    order_id,
    user_name,
    user_email,
    user_contact,
    user_address,
    theme,
    retry,
    backend_endpoint,
    policy_number,
    premium,
  }: any) {
    let options: any = {
      key: key_id,
      provider: provider,
      amount: amount?.toString(),
      currency: currency,
      order_id: order_id,
      retry: retry,
      modal: {
        ondismiss: () => {
          eventBus()?.getEventBus()?.emit(FIAT_TX_MODAL_DISMISSED, null, {});
        },
      },
    };
    if (name) {
      options.name = name;
    }
    if (description) {
      options.description = description;
    }
    if (image) {
      options.image = { logo: image };
    }
    options.handler = async (response: any) => {
      const data: any = {
        orderCreationId: order_id,
        razorpayPaymentId: response?.razorpay_payment_id,
        razorpayOrderId: response?.razorpay_order_id,
        razorpaySignature: response?.razorpay_signature,
        policyNumber: policy_number || response?.razorpay_order_id,
        email: user_email,
        phoneVerified: true,
        premium: premium,
      };
      try {
        if (
          backend_endpoint &&
          backend_endpoint.trim() !== '' &&
          !backend_endpoint.includes('pooja-dev-dc')
        ) {
          let sttp = Sttp.withBaseUrl(backend_endpoint);
          const res = await sttp.post('', { ...data });
          if (res?.data()) {
            eventBus()?.getEventBus()?.emit(FIAT_TX_SUCCESS, null, {
              ...data,
              ...(res.data() || {}),
            });
            return;
          }
        }
      } catch (error: any) {
        console.warn('Backend payment verification notification error (non-fatal):', error?.message);
      }
      // Razorpay payment completed successfully
      eventBus()?.getEventBus()?.emit(FIAT_TX_SUCCESS, null, data);
    };
    if (user_name || user_email || user_contact) {
      if (user_name) {
        options.prefill = {
          name: user_name,
        };
      }
      if (user_email) {
        options.prefill = {
          ...(options.prefill || {}),
          email: user_email,
        };
      }
      if (user_contact) {
        options.prefill = {
          ...(options.prefill || {}),
          contact: user_contact,
        };
      }
    }
    if (user_address) {
      options.notes = {
        address: user_address,
      };
    }
    if (theme) {
      options.theme = {
        color: theme,
      };
    }
    return options;
  }
}
